import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormLabel from '@mui/material/FormLabel';
import Button from '@mui/material/Button';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    iconButton: {
        cursor: 'pointer',
    }
}));

const UI = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [ui, setUI] = useState({
        lookups: [], //{name: 'WHOIS', url: 'https://dnsquery.org/ipwhois/$addr'}
        file_lookups: [], //{name: 'WHOIS', url: 'https://www.virustotal.com/gui/file/$file/detection'}
        messages: {
            header: '',
            footer: '',
            signin: '',
        }
    });
    const [original, setOriginal] = useState();
    const [changes, setChanges] = useState(false);
    const [kick, setKick] = useState(false); // FML
    const lookupForm = useValidate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const uiPerms = Auth.permissions.get('UI');

    useEffect(() => {
        setChanges(!_isEqual(ui, original));
    }, [original, ui]);

    useEffect(() => {
        if (systemSettings && systemSettings.ui) {
            const m = _cloneDeep(systemSettings.ui.messages);
            const u = {
                ..._cloneDeep(systemSettings.ui),
                messages: {
                    header: m.header ? m.header : '',
                    footer: m.footer ? m.footer : '',
                    signin: m.signin ? m.signin : '',
                },
            };
            setOriginal(_cloneDeep(u));
            setUI(u);
        }
    }, [systemSettings]);

    const handleHeader = (e) => {
        const u = { ...ui };
        ui.messages.header = e.currentTarget.value;
        setUI(u);
    };

    const handleFooter = (e) => {
        const u = { ...ui };
        ui.messages.footer = e.currentTarget.value;
        setUI(u);
    };

    const handleSignIn = (e) => {
        const u = { ...ui };
        ui.messages.signin = e.currentTarget.value;
        setUI(u);
    };

    const handleAddLookup = key => {
        const u = { ...ui, [key]: ui[key] || [] };
        u[key].push({ name: '', url: '' });
        setUI(u);
    };

    const handleRemoveLookup = (key, idx) => {
        const u = { ...ui, [key]: ui[key] || [] };
        u[key].splice(idx, 1);
        setUI(u);
    };

    const handleLookupName = (e, key, idx, validID, valid) => {
        lookupForm.set(current => ({ ...current, [validID]: valid }));

        const u = { ...ui, [key]: ui[key] || [] };
        u[key][idx].name = e.target.value;
        setUI(u);
    };

    const handleLookupURL = (e, key, idx, validID, valid) => {
        lookupForm.set(current => ({ ...current, [validID]: valid }));

        const u = { ...ui, [key]: ui[key] || [] };
        u[key][idx].url = e.target.value;
        setUI(u);
    };

    const handleSave = () => {
        // both API and context will merge on update
        // context update will also toast the user
        updateSystemSettings({ ui });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    // so dumb that these two can't be combined
    useEffect(() => {
        // to force a re-render
        setKick(lookupForm.kicker);
    }, [lookupForm.kicker]);

    const isValid = useMemo(() => {
        console.debug(kick);
        return changes && checkValidity(lookupForm.get());
    }, [changes, lookupForm, kick]);

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <Grid container spacing={5}>
                    <Grid item xs={4}>
                        <TextField
                            disabled={!uiPerms.write}
                            label="Header Message"
                            aria-label="header message"
                            fullWidth
                            helperText="Display at the top of every screen."
                            value={ui.messages.header}
                            onChange={handleHeader}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            disabled={!uiPerms.write}
                            label="Footer Message"
                            aria-label="footer message"
                            fullWidth
                            helperText="Displayed at the bottom of every screen."
                            value={ui.messages.footer}
                            onChange={handleFooter}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            disabled={!uiPerms.write}
                            label="Sign-in Message"
                            aria-label="sign-in message"
                            fullWidth
                            helperText="Displayed on the sign-in screen."
                            value={ui.messages.signin}
                            onChange={handleSignIn}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {uiPerms.write && <Box display="flex" style={{ justifyContent: 'space-between' }}>
                            <Typography variant="h6">Lookup URLs</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="add url"
                                onClick={() => handleAddLookup('lookups')}>
                                <AddIcon />Add URL
                            </Button>
                        </Box>}
                        <Box>
                            <FormLabel>Map of named address lookup URLs. Use <code>$addr</code> to indicate the address.</FormLabel>
                        </Box>
                        <Box mt={3}>
                            <Grid container spacing={2}>
                                <Grid container item xs={12}>
                                    <Grid item xs={6}>Name</Grid>
                                    <Grid item xs={6}>URL</Grid>
                                </Grid>
                                {(ui.lookups || []).map((v, i) => {
                                    return (
                                        <Grid key={'lookup-url-' + i} container item xs={12} spacing={2}>
                                            <Grid item xs={6}>
                                                <Validator
                                                    fullWidth
                                                    allowEmpty={false}
                                                    disabled={!uiPerms.write}
                                                    aria-label="lookup tool title"
                                                    placeholder="Lookup tool title"
                                                    value={v.name}
                                                    kick={changes}
                                                    unique={ui.lookups.map(x => x.name)}
                                                    onChange={(e, validID, valid) => {
                                                        handleLookupName(e, 'lookups', i, validID, valid);
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Validator
                                                    fullWidth
                                                    allowEmpty={false}
                                                    disabled={!uiPerms.write}
                                                    aria-label="tool url"
                                                    placeholder="https://example.com/?q=$addr"
                                                    value={v.url}
                                                    valueType="host"
                                                    kick={changes}
                                                    onChange={(e, validID, valid) => {
                                                        handleLookupURL(e, 'lookups', i, validID, valid);
                                                    }}
                                                    endAdornment={!uiPerms.write ? null
                                                        : <DeleteIcon
                                                            className={classes.iconButton}
                                                            aria-label="remove lookup"
                                                            onClick={() => handleRemoveLookup('lookups', i)} />
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    </Grid>
                    {/* file hash lookup urls */}
                    <Grid item xs={12}>
                        {uiPerms.write && <Box display="flex" style={{ justifyContent: 'space-between' }}>
                            <Typography variant="h6">File Hash Lookup URLs</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="add url"
                                onClick={() => handleAddLookup('file_lookups')}>
                                <AddIcon />Add URL
                            </Button>
                        </Box>}
                        <Box>
                            <FormLabel>Map of file hash lookup URLs. Use <code>$file</code> to indicate the file hash.</FormLabel>
                        </Box>
                        <Box mt={3}>
                            <Grid container spacing={2}>
                                <Grid container item xs={12}>
                                    <Grid item xs={6}>Name</Grid>
                                    <Grid item xs={6}>URL</Grid>
                                </Grid>
                                {(ui.file_lookups || []).map((v, i) => {
                                    return (
                                        <Grid key={'file-lookup-url-' + i} container item xs={12} spacing={2}>
                                            <Grid item xs={6}>
                                                <Validator
                                                    fullWidth
                                                    allowEmpty={false}
                                                    disabled={!uiPerms.write}
                                                    aria-label="lookup tool title"
                                                    placeholder="Lookup tool title"
                                                    value={v.name}
                                                    kick={changes}
                                                    unique={(ui.file_lookups || []).map(x => x.name)}
                                                    onChange={(e, validID, valid) => {
                                                        handleLookupName(e, 'file_lookups', i, validID, valid);
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Validator
                                                    fullWidth
                                                    allowEmpty={false}
                                                    disabled={!uiPerms.write}
                                                    aria-label="tool url"
                                                    placeholder="https://example.com/?q=$file"
                                                    value={v.url}
                                                    valueType="host"
                                                    kick={changes}
                                                    onChange={(e, validID, valid) => {
                                                        handleLookupURL(e, 'file_lookups', i, validID, valid);
                                                    }}
                                                    endAdornment={!uiPerms.write ? null
                                                        : <DeleteIcon
                                                            className={classes.iconButton}
                                                            aria-label="remove lookup"
                                                            onClick={() => handleRemoveLookup('file_lookups', i)} />
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    </Grid>
                    {uiPerms.write && <Grid item xs={12}>
                        <Button
                            className={classes.buttonRight}
                            variant="contained"
                            color="primary"
                            aria-label="save"
                            onClick={handleSave}
                            disabled={!isValid}
                        >Save</Button>
                        <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                    </Grid>}
                </Grid>
            </Box>
        </Paper>
    );
};

export default UI;
